import { LineHeightIcon } from '@radix-ui/react-icons';
import { Button, Flex, IconButton, Popover } from '@radix-ui/themes';
import {
  SortAscIcon,
  SortDescIcon,
} from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import useOnClickAway from 'hooks/useOnClickAway';
import { t } from 'i18next';
import { IIdentifiedComponent } from 'interfaces/i-components';
import {
  RADIX,
  RadixBtnSize,
  RadixBtnVariant,
  RadixColor,
} from 'lib_ts/enums/radix-ui';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IOption, ISortSelection } from 'lib_ts/interfaces/common/i-option';
import { FC, useCallback, useMemo, useRef, useState } from 'react';

interface IStyleProps {
  className: string;
  size: RadixBtnSize;
  variant: RadixBtnVariant;
  color: RadixColor;
}

interface IConfigProps {
  // shown when there is no selection
  label: string;

  value: Nullable<ISortSelection>;
  options: IOption[];

  onChange: (v: ISortSelection) => void;

  disabled?: boolean;
}

type IProps = IIdentifiedComponent & IConfigProps & Partial<IStyleProps>;

export const CommonSortSelect: FC<IProps> = (props) => {
  const [open, setOpen] = useState(false);

  /*
      Close the wrapping popover clicking outside of it:
      
      Radix Popover handles this fine on desktop, but there are certain cases
      where the popover won't close properly on mobile devices (i.e. when you click a table element)
    */

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const onClickAway = useCallback(() => {
    setOpen(false);
  }, []);

  useOnClickAway({
    ref: popoverRef,
    shouldListen: open,
    onClickAway,
    // don't handle clicks on the trigger element to prevent re-opening popover
    ignoreEls: [triggerRef.current],
  });

  const selected = props.value;

  const selectedOption = useMemo(
    () => props.options.find((o) => o.value === props.value?.value),
    [props.options, props.value]
  );

  const activeIcon = useMemo(() => {
    switch (props.value?.dir) {
      case 1: {
        return <SortAscIcon />;
      }

      case -1: {
        return <SortDescIcon />;
      }

      default: {
        return <LineHeightIcon />;
      }
    }
  }, [props.value?.dir]);

  return (
    <ErrorBoundary identifier={props.identifier}>
      <Popover.Root open={open} onOpenChange={(value) => setOpen(value)}>
        <Popover.Trigger disabled={props.disabled} ref={triggerRef}>
          <Button
            size={props.size}
            variant={props.variant}
            color={props.color}
            className={props.className}
          >
            <Flex gap="2" align="center">
              {activeIcon}
              {t(selectedOption?.label ?? props.label)}
            </Flex>
          </Button>
        </Popover.Trigger>
        <Popover.Content className="BorderedContent" ref={popoverRef}>
          <Flex
            p="2"
            direction="column"
            gap={RADIX.FLEX.GAP.FORM}
            style={{ minWidth: '200px' }}
          >
            {props.options.map((o) => {
              const isUpActive =
                selected?.value === o.value && selected.dir === 1;
              const isDownActive =
                selected?.value === o.value && selected.dir === -1;

              return (
                <Flex key={`o-${o.value}`} align="center" gap="1">
                  <Flex flexGrow="1" align="center">
                    {t(o.label)}
                  </Flex>

                  <IconButton
                    className={isUpActive ? undefined : 'btn-floating'}
                    variant={
                      isUpActive
                        ? RADIX.BUTTON.VARIANT.SELECTED
                        : RADIX.BUTTON.VARIANT.NOT_SELECTED
                    }
                    size="1"
                    color={
                      isUpActive ? RADIX.COLOR.ACCENT : RADIX.COLOR.NEUTRAL
                    }
                    onClick={() => {
                      if (isUpActive) {
                        // already the active mode
                        return;
                      }

                      props.onChange({
                        value: o.value,
                        dir: 1,
                      });
                    }}
                  >
                    <SortAscIcon />
                  </IconButton>

                  <IconButton
                    className={isDownActive ? undefined : 'btn-floating'}
                    variant={
                      isDownActive
                        ? RADIX.BUTTON.VARIANT.SELECTED
                        : RADIX.BUTTON.VARIANT.NOT_SELECTED
                    }
                    size="1"
                    color={
                      isDownActive ? RADIX.COLOR.ACCENT : RADIX.COLOR.NEUTRAL
                    }
                    onClick={() => {
                      if (isDownActive) {
                        // already the active mode
                        return;
                      }

                      props.onChange({
                        value: o.value,
                        dir: -1,
                      });
                    }}
                  >
                    <SortDescIcon />
                  </IconButton>
                </Flex>
              );
            })}
          </Flex>
        </Popover.Content>
      </Popover.Root>
    </ErrorBoundary>
  );
};
